import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { secondsToHms, hmsToSeconds, isValidTimeFormat } from "./utils";

const MAX_HH = 999;

interface TimeInputProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  totalTime: number;
  isStart: boolean;
  onError?: (hasError: boolean, message: string) => void;
  secondTime: number;
  isUpdatingTrimTime: boolean;
  setIsUpdatingTrimTime: (isUpdating: boolean) => void;
  seekVideo: any;
}

const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  disabled = false,
  totalTime,
  isStart,
  onError,
  secondTime,
  isUpdatingTrimTime,
  setIsUpdatingTrimTime,
  seekVideo,
}) => {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(secondsToHms(value));
  const errorRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevValueRef = useRef(value);
  const cursorRef = useRef<number | null>(null);

  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      errorRef.current = false;
      onError && onError(false, "");
      setEditing(false);
      setInputValue(secondsToHms(value));
      setIsUpdatingTrimTime(false);
    }
  }, [onError, setIsUpdatingTrimTime, value]);

  const handleClick = () => {
    if (disabled || isUpdatingTrimTime) return;
    setEditing(true);
    setIsUpdatingTrimTime(true);
    setTimeout(() => {
      inputRef.current && inputRef.current.focus();
    }, 0);
  };

  const handleBlur = () => {
    try {
      if (inputValue.length === 0) {
        errorRef.current = false;
        onError && onError(false, "");

        if (isStart) {
          onChange(0);
          setInputValue("00:00:00");
          prevValueRef.current = 0;
        } else {
          onChange(totalTime);
          setInputValue(secondsToHms(totalTime));
          prevValueRef.current = totalTime;
        }
        return;
      }

      if (errorRef.current) return;

      const seconds = hmsToSeconds(inputValue);
      if (!isNaN(seconds)) {
        // Only call seekVideo if we have a valid time
        seekVideo(seconds);
      }

      setEditing(false);
      setIsUpdatingTrimTime(false);
    } catch (error) {
      console.error("Error in handleBlur:", error);
      setEditing(false);
      setIsUpdatingTrimTime(false);
    }
  };

  useLayoutEffect(() => {
    if (cursorRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorRef.current, cursorRef.current);
      cursorRef.current = null; // Reset
    }
  }, [inputValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9:]/g, "");

    const cursorPos = e.target.selectionStart;
    cursorRef.current = cursorPos;

    setIsUpdatingTrimTime(true);
    setInputValue(val);

    errorRef.current = false;
    onError && onError(false, "");

    if (!isValidTimeFormat(val)) {
      errorRef.current = true;
      onError && onError(true, "Invalid format: Use (e.g. 1, 1:20, 1:02:45)");
      return;
    }
    const secs = hmsToSeconds(val);
    if (!isNaN(secs)) {
      // For end time, if it exceeds totalTime, set it to totalTime
      if (!isStart && secs > totalTime) {
        setInputValue(secondsToHms(totalTime));
        prevValueRef.current = totalTime;
        onChange(totalTime);
        return;
      }
      checkTimeValidity(secs);
    } else {
      errorRef.current = true;
      onError && onError(true, "Invalid Time");
    }
  };

  const checkTimeValidity = (secs: number) => {
    if (!onError) return;

    if (isStart && secs > secondTime) {
      errorRef.current = true;
      onError(true, "Start time cannot be greater than End time");
    } else if (!isStart && secs < secondTime) {
      errorRef.current = true;
      onError(true, "End time cannot be less than start time");
    } else if (Math.abs(secs - secondTime) < 5) {
      errorRef.current = true;
      onError(
        true,
        "Difference between start and end time cannot be less than 5 seconds"
      );
    } else {
      errorRef.current = false;
      onError(false, "");
      prevValueRef.current = secs;
      onChange(secs);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
      return;
    }

    // For KeyUp or KeyDown
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();

      const isUp = e.key === "ArrowUp";
      const parts = inputValue.split(":").map(Number); // 01:20:00
      let [hh, mm, ss] = [0, 0, 0];

      // Normalize parts to HH:MM:SS
      if (parts.length === 1) {
        ss = parts[0];
      } else if (parts.length === 2) {
        [mm, ss] = parts;
      } else if (parts.length === 3) {
        [hh, mm, ss] = parts;
      }

      // For Cursor Position
      const cursorPos = e.currentTarget.selectionStart || 0;
      // Find which part of the time to be increased
      const partIndex = getTimePartIndex(cursorPos, inputValue);

      // Adjust the time parts
      [hh, mm, ss] = adjustTimeParts(hh, mm, ss, partIndex, isUp);

      const newVal = hh * 3600 + mm * 60 + ss;

      // For end time, if it exceeds totalTime, set it to totalTime
      if (!isStart && newVal > totalTime) {
        setInputValue(secondsToHms(totalTime));
        prevValueRef.current = totalTime;
        onChange(totalTime);
        return;
      }

      checkTimeValidity(newVal);
      cursorRef.current = cursorPos;
      setInputValue(secondsToHms(newVal));
    }
  };

  const adjustTimeParts = (
    hh: number,
    mm: number,
    ss: number,
    partIndex: number,
    isUp: boolean
  ): [number, number, number] => {
    if (isStart && !isUp) {
      const newVal = hh * 3600 + mm * 60 + ss;
      if (newVal <= 0) return [0, 0, 0];
    }
    if (partIndex === 0) {
      hh = adjust(hh, isUp, MAX_HH);
    } else if (partIndex === 1) {
      mm = adjust(mm, isUp, 59);
      if (isUp && mm === 0) {
        hh = adjust(hh, true, MAX_HH);
      }
      if (!isUp && mm === 59) {
        hh = adjust(hh, false, MAX_HH);
      }
    } else if (partIndex === 2) {
      ss = adjust(ss, isUp, 59);
      if (isUp && ss === 0) {
        mm = adjust(mm, true, 59);
        if (mm === 0) {
          hh = adjust(hh, true, MAX_HH);
        }
      }
      if (!isUp && ss === 59) {
        mm = adjust(mm, false, 59);
        if (mm === 59) {
          hh = adjust(hh, false, MAX_HH);
        }
      }
    }
    return [hh, mm, ss];
  };

  const adjust = (val: number, isUp: boolean, max: number = MAX_HH): number => {
    if (isUp) return val + 1 > max ? 0 : val + 1;
    return val - 1 < 0 ? max : val - 1;
  };

  // Helper to get index of time part (0=hh, 1=mm, 2=ss)
  const getTimePartIndex = (cursorPos: number, val: string): number => {
    const colonsBefore = (val.slice(0, cursorPos).match(/:/g) || []).length;
    const totalColons = (val.match(/:/g) || []).length;
    if (totalColons === 0) return 2; // only seconds
    if (totalColons === 1) return colonsBefore === 0 ? 1 : 2; // MM:SS
    return colonsBefore; // HH:MM:SS
  };

  const isDurationInHours = totalTime > 3600;

  const getFormattedDuration = (duration: number): string => {
    // Always return HH:MM:SS format
    return secondsToHms(duration);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{
        width: "65px",
        textAlign: "center",
        border: errorRef.current ? "2px solid red" : "1px solid #ccc",
        outline: "none",
        padding: "2px 4px",
        borderRadius: "4px",
        cursor: disabled ? "not-allowed" : "text",
      }}
      disabled={disabled}
    />
  );
};

export default TimeInput;
